import io from "socket.io-client";
import { getBackendUrl } from "../config";
import api from "../services/api";

class SocketWorker {
  constructor(companyId, userId) {
    const sessionToken = this.getSessionToken();

    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.token = sessionToken;
      this.socket = null;
      this.eventListeners = {};
      this.configureSocket();
      SocketWorker.instance = this;
    } else {
      SocketWorker.instance.updateConnection(companyId, userId, sessionToken);
    }

    return SocketWorker.instance;
  }

  updateConnection(companyId, userId, token) {
    if (!companyId || !userId) {
      return;
    }

    const didChange =
      this.companyId !== companyId ||
      this.userId !== userId ||
      this.token !== token;

    if (!didChange) return;

    this.companyId = companyId;
    this.userId = userId;
    this.token = token || this.getSessionToken();
    this.configureSocket();
  }

  getSessionToken() {
    const headerToken = api?.defaults?.headers?.Authorization;
    if (headerToken && String(headerToken).trim()) {
      return String(headerToken).trim();
    }

    const rawToken = localStorage.getItem("token");
    if (!rawToken) {
      return "";
    }

    try {
      const parsed = JSON.parse(rawToken);
      const str = String(parsed || "").trim();
      return str.startsWith("Bearer ") ? str : `Bearer ${str}`;
    } catch (_) {
      const str = String(rawToken || "").trim();
      return str.startsWith("Bearer ") ? str : `Bearer ${str}`;
    }
  }

  configureSocket() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const queryToken = this.token || this.getSessionToken();
    if (!this.companyId || !this.userId || !queryToken) {
      return;
    }

    const namespace = `/${this.companyId}`;

    this.socket = io(`${getBackendUrl()}${namespace}`, {
      autoConnect: true,
      // Mantem websocket preferencial, mas permite fallback para polling
      // quando proxy/rede não suporta upgrade WS.
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      query: { userId: this.userId, token: queryToken },
    });

    // Reaplica listeners registrados pela aplicação quando o socket é recriado.
    Object.entries(this.eventListeners || {}).forEach(([event, callbacks]) => {
      if (!Array.isArray(callbacks)) return;
      callbacks.forEach((callback) => {
        this.socket.on(event, callback);
      });
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO");
      console.log("Motivo da desconexão:", reason);
    });

    this.socket.on("connect_error", (error) => {
      console.log("Erro de conexão Socket.IO:", error?.message || error);

      const errorMessage = String(error?.message || "").toLowerCase();
      const authLikeError =
        errorMessage.includes("token") ||
        errorMessage.includes("jwt") ||
        errorMessage.includes("unauthorized") ||
        errorMessage.includes("auth");

      if (authLikeError) {
        const refreshedToken = this.getSessionToken();
        if (refreshedToken && refreshedToken !== this.token) {
          this.token = refreshedToken;
          if (this.socket?.io?.opts) {
            this.socket.io.opts.query = {
              userId: this.userId,
              token: this.token,
            };
          }
          if (!this.socket.connected) {
            this.socket.connect();
          }
        }
      }
    });

    if (this.socket?.io) {
      this.socket.io.on("reconnect_attempt", () => {
        this.token = this.getSessionToken();
        if (this.socket?.io?.opts) {
          this.socket.io.opts.query = {
            userId: this.userId,
            token: this.token,
          };
        }
      });
    }
  }

  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      if (callback) {
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(
          (cb) => cb !== callback
        );
      } else {
        this.eventListeners[event].forEach((cb) => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.eventListeners = {};
      SocketWorker.instance = null;
      console.log("Socket desconectado manualmente");
    }
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
      return;
    }

    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  forceReconnect() {
    this.configureSocket();
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
